import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { IconPlus, IconX } from "@tabler/icons-react";
import { Button, Checkbox, IconButton, Input, Select, Text } from "@/components/ui";
import { rolesAdminQueryOptions } from "@/features/admin-roles/hooks/use-roles-admin";
import { listarUsuariosAdmin } from "@/features/admin-usuarios/services/usuarios-service";
import type { Canal, Destinatario, TipoDestinatario } from "../../../services/notificaciones-service";
import { AYUDA_DESTINATARIO, ETIQUETA_DESTINATARIO } from "../../../utils/etiquetas";

/** Canales con destinatario fijo y cómo se captura (el resto solo admite usuarios del portal). */
const FIJO_POR_CANAL: Partial<Record<Canal, { tipo: string; placeholder: string }>> = {
  Email: { tipo: "email", placeholder: "correo@proveedores.test" },
  Sms: { tipo: "tel", placeholder: "+525512345678" },
  WhatsApp: { tipo: "tel", placeholder: "+525512345678" },
  Telegram: { tipo: "text", placeholder: "Chat id (123456789) o @canal" },
};

const TIPOS_USUARIO: TipoDestinatario[] = [
  "SolicitanteODueno", "Actor", "AutorizadoresDelNivel", "UsuariosConRol", "UsuarioEspecifico", "ResponsablesDelTipoCatalogo",
  "RevisoresDocumentales",
];

/** Lista de destinatarios de una notificación: fijos (solo correo) y dinámicos (usuarios del portal). */
export function DestinatariosEditor({
  canal, destinatarios, onCambio,
}: { canal: Canal; destinatarios: Destinatario[]; onCambio: (d: Destinatario[]) => void }) {
  const fijo = FIJO_POR_CANAL[canal];
  const tipos: TipoDestinatario[] = fijo ? ["Fijo", ...TIPOS_USUARIO] : TIPOS_USUARIO;
  const [nuevo, setNuevo] = useState<TipoDestinatario>(tipos[0]);
  const actualizar = (i: number, cambio: Partial<Destinatario>) =>
    onCambio(destinatarios.map((d, j) => (j === i ? { ...d, ...cambio } : d)));

  return (
    <div className="space-y-2">
      {destinatarios.length === 0 ? <Text size="xs" c="dimmed">Sin destinatarios.</Text> : null}
      {destinatarios.map((d, i) => (
        <div key={i} className="rounded-md border border-border p-2">
          <div className="flex items-center justify-between gap-2">
            <Text size="xs" fw={600}>{ETIQUETA_DESTINATARIO[d.tipo]}</Text>
            <IconButton size="xs" aria-label="Quitar destinatario" onClick={() => onCambio(destinatarios.filter((_, j) => j !== i))}>
              <IconX />
            </IconButton>
          </div>
          <Text size="xs" c="dimmed">{AYUDA_DESTINATARIO[d.tipo]}</Text>
          {d.tipo === "Fijo" && fijo ? (
            <Input className="mt-1.5" size="xs" type={fijo.tipo} placeholder={fijo.placeholder} value={d.valor ?? ""} onChange={(e) => actualizar(i, { valor: e.target.value.trim() })} />
          ) : null}
          {d.tipo === "UsuariosConRol" ? (
            <SelectorRol valor={d.valor ?? ""} soloOrg={!!d.soloOrganizacionDelEvento} onCambio={(c) => actualizar(i, c)} />
          ) : null}
          {d.tipo === "UsuarioEspecifico" ? <SelectorUsuario valor={d.valor ?? ""} onCambio={(valor) => actualizar(i, { valor })} /> : null}
        </div>
      ))}
      <div className="flex gap-2">
        <Select
          size="xs"
          wrapperClassName="flex-1"
          value={nuevo}
          onValueChange={(v) => setNuevo(v as TipoDestinatario)}
          options={tipos.map((t) => ({ value: t, label: ETIQUETA_DESTINATARIO[t] }))}
        />
        <Button size="xs" variant="default" leftSection={<IconPlus size={12} />} onClick={() => onCambio([...destinatarios, { tipo: nuevo, valor: null, soloOrganizacionDelEvento: false }])}>
          Agregar
        </Button>
      </div>
    </div>
  );
}

function SelectorRol({ valor, soloOrg, onCambio }: { valor: string; soloOrg: boolean; onCambio: (c: Partial<Destinatario>) => void }) {
  // Hotspot 88 · `/admin/roles` se lee también con `admin.notificaciones`; si aun así falla,
  // se avisa en vez de dejar el selector vacío sin explicación.
  const { data: rolesData, isError, isPending } = useQuery(rolesAdminQueryOptions);
  const roles = (rolesData ?? []).map((r) => r.nombre);
  return (
    <div className="mt-1.5 space-y-1.5">
      {isError ? <AvisoCatalogo texto="No se pudo cargar la lista de roles." /> : null}
      <Select size="xs" placeholder={isPending ? "Cargando…" : "Elige un rol"} disabled={isPending} value={valor || undefined} onValueChange={(v) => onCambio({ valor: v })} options={roles.map((r) => ({ value: r, label: r }))} />
      <Checkbox label="Solo de la organización del evento" checked={soloOrg} onCheckedChange={(c) => onCambio({ soloOrganizacionDelEvento: c === true })} />
    </div>
  );
}

function SelectorUsuario({ valor, onCambio }: { valor: string; onCambio: (v: string) => void }) {
  const { data, isError, isPending } = useQuery({
    queryKey: ["admin", "notificaciones", "usuarios-activos"],
    queryFn: async () => {
      const todos = [];
      for (let page = 1; ; page++) {
        const r = await listarUsuariosAdmin({ page, pageSize: 100 });
        todos.push(...r.items);
        if (todos.length >= r.total || r.items.length === 0) break;
      }
      return todos.filter((u) => u.activo);
    },
    staleTime: 5 * 60_000,
  });
  return (
    <>
      {isError ? <AvisoCatalogo texto="No se pudo cargar la lista de usuarios." /> : null}
      <Select
        size="xs"
        searchable
        wrapperClassName="mt-1.5"
        placeholder={isPending ? "Cargando…" : "Elige un usuario"}
        disabled={isPending}
        value={valor || undefined}
        onValueChange={onCambio}
        options={(data ?? []).map((u) => ({ value: u.id, label: u.fullName ? `${u.fullName} · ${u.email}` : u.email }))}
      />
    </>
  );
}

function AvisoCatalogo({ texto }: { texto: string }) {
  return (
    <p className="mt-1.5 text-xs text-peligro-700 dark:text-peligro-400">
      {texto} Verifica tu permiso o intenta de nuevo más tarde.
    </p>
  );
}
